import React, { useState } from "react";
import { useSelector } from "react-redux";
import { SectionToggleWrapper } from "./SectionToggleWrapper";
import PieCharts from "./PieCharts";
import { stripHTMLTagsFromText } from "../../../../utils/formatters";
import { DataPoints } from "./DataPoints";
import BiAxialLineChart from "../../../../components/biAxialLineChart/biAxialLineChart";
import LogsModal from "./LogsModal";
import TableWithCustomSearch from "../../../../components/coreUiTableWithCustomSearchAndDateConversion/TableWithCustomSearch";
import ReactTooltip from "react-tooltip";

const ClientInfoContent = () => {
  const {
    clientInfo: { sourceType },
    clientVisits,
    clientSales,
    clientPastDues,
    clientMembershipTypes,
    clientProspectTypes,
    clientContactLogs,
    clientCharts,
    clientContracts,
    clientCheckIns,
    contractStatuses,
    contractFreezes,
    remainingSessions,
    currentUser: { currencySymbol },
    apiError,
    loadingModules
  } = useSelector((state) => {
    return state.clientProfileModule;
  });
  const [modalContent, setModalContent] = useState(null);
  const closeModal = () => {
    setModalContent(null);
  };
  const toggleModal = (content = "") => {
    setModalContent(content);
  };

  const clientInfoSections = () => {
    const salesTableTitle = sourceType === "mindbody" ? "Accrual Sales" : "Sales";

    return (
      <>
        <div className="client-info-bottom-wrapper ">
          <span id="clientProfileModulesWrapper">
            <SectionToggleWrapper
              title="Sales and Visits Data"
              idForConfigurator="salesandvisitsdata"
              isLoading={loadingModules?.salesAndVisitsData}
            >
              <div className="client-info-bottom chart">
                <div className="cell">
                  <BiAxialLineChart chartData={clientCharts} />
                </div>
              </div>
            </SectionToggleWrapper>
            <SectionToggleWrapper
              title="Contracts/Agreements"
              data={clientContracts}
              fileName="contracts-agreements"
              idForConfigurator="contractsagreements"
              isLoading={loadingModules?.contractsAgreements}
              overrideColumnCallback={(header) => ({
                sorter: header !== "End Date"
              })}
            />
            <SectionToggleWrapper
              title="Freezes"
              data={contractFreezes}
              idForConfigurator="freezes"
              fileName="freezes"
              isLoading={loadingModules?.freezes}
            />
            <SectionToggleWrapper
              title="Remaining Sessions"
              data={remainingSessions}
              idForConfigurator="remainingsessions"
              fileName="remaining-sessions"
              isLoading={loadingModules?.remainingSessions}
            />
            <SectionToggleWrapper
              title={`Membership Statuses${contractStatuses.membershipStatusLabel || ''}`}
              data={contractStatuses}
              fileName="membership-statuses"
              idForConfigurator="membershipstatuses"
              isLoading={loadingModules?.membershipStatuses}
              overrideColumnCallback={(header) => ({
                sorter: header !== "End Date"
              })}
            />
            <SectionToggleWrapper
              title="Membership Types"
              data={clientMembershipTypes}
              fileName="membership-types"
              idForConfigurator="membershiptypes"
              isLoading={loadingModules?.membershipTypes}
              overrideColumnCallback={(header) => ({
                sorter: header !== "End Date"
              })}
            />
            <SectionToggleWrapper
              title="Prospect Types"
              data={clientProspectTypes}
              idForConfigurator="prospectypes"
              fileName="prospect-types"
              isLoading={loadingModules?.prospectypes}
            />
            <SectionToggleWrapper
              title={salesTableTitle}
              data={clientSales}
              currencySymbol={currencySymbol}
              fileName="sales"
              idForConfigurator="sales"
              isLoading={loadingModules?.sales}
              overrideColumnCallback={(header) => ({
                sorter: header !== "Exp Date"
              })}
            />
            <SectionToggleWrapper
              title="Past Dues"
              data={clientPastDues}
              idForConfigurator="pastdues"
              fileName="past-dues"
              isLoading={loadingModules?.pastdues}
            />
            <SectionToggleWrapper
              title="Visits"
              data={clientVisits}
              idForConfigurator="visits"
              fileName="visits"
              isLoading={loadingModules?.visits}
            />
            <SectionToggleWrapper
              title="Check-Ins"
              data={clientCheckIns}
              idForConfigurator="checkins"
              fileName="check-ins"
              isLoading={loadingModules?.clientCheckIns}
            />
            <SectionToggleWrapper
              title="Contact Logs"
              idForConfigurator="contactlogs"
              isLoading={loadingModules?.contactLogs}
            >
              {clientContactLogs?.tableInfo?.data.length > 0 ? (
                <>
                  <DataPoints dataPoints={clientContactLogs.dataPoints} />
                  <ReactTooltip effect="solid" />

                  <TableWithCustomSearch
                    title="Contact Logs"
                    tableWrapperId="client-contact-logs"
                    data={clientContactLogs.tableInfo.data}
                    colHeaders={clientContactLogs.tableInfo.colHeaders}
                    columns={clientContactLogs.tableInfo.columns}
                    showCount={false}
                    stripHTMLTagsInColumns={[4]}
                    rawDataLink={clientContactLogs.tableInfo.rawDataLink}
                    onPageChange={() => {
                      setTimeout(() => {
                        ReactTooltip.rebuild();
                      }, 0);
                    }}
                    fileName="contact-logs"
                    overrideColumnCallback={(header) => ({
                      _style: header === "Description" ? { width: "30%" } : {}
                    })}
                    customFn={(header, formattedValue) => {
                      switch (header) {
                        case "Staff Name": {
                          let rowText = stripHTMLTagsFromText(formattedValue);
                          return (
                            <div className="wrapped-text" data-tip={rowText}>
                              {rowText}
                            </div>
                          );
                        }
                        case "Description": {
                          let rowText = stripHTMLTagsFromText(formattedValue).substring(0, 100);
                          return (
                            <div className="text-ellipsis">
                              {rowText}{" "}
                              <a
                                className="contact-logs-view-message"
                                onClick={() => toggleModal(formattedValue)}
                              >
                                View
                              </a>
                            </div>
                          );
                        }
                        default:
                          return formattedValue;
                      }
                    }}
                  />
                </>
              ) : (
                <span className="error">No data</span>
              )}
            </SectionToggleWrapper>
            <SectionToggleWrapper
              title="Pie Charts"
              idForConfigurator="piecharts"
              isLoading={loadingModules?.pieCharts}
            >
              <PieCharts />
            </SectionToggleWrapper>
          </span>
        </div>
      </>
    );
  };

  if (apiError) {
    throw new Error("api url has error");
  }

  return (
    <>
      <LogsModal modalContent={modalContent} closeModal={closeModal} />
      {clientInfoSections()}
    </>
  );
};

export default ClientInfoContent;
